import { useState, useCallback } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { useT } from '../../i18n/LocaleContext'
import { INITIAL_ANSWERS, type DevisAnswers } from './types'
import { submitDevisBrief } from './submitDevisBrief'
import StepProducts from './StepProducts'
import StepVolumes from './StepVolumes'
import StepContact from './StepContact'
import StepBooking from './StepBooking'

const STEPS = 4 // 0..2 inputs + 3 booking

const EASE = [0.22, 1, 0.36, 1] as const

export default function QuoteWizard({ active = true }: { active?: boolean }) {
  const { t } = useT()
  const reduce = useReducedMotion()
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<DevisAnswers>(INITIAL_ANSWERS)
  const [booked, setBooked] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const update = useCallback((patch: Partial<DevisAnswers>) => setAnswers((a) => ({ ...a, ...patch })), [])
  const onBooked = useCallback(() => setBooked(true), [])

  const next = () => {
    setError(null)
    if (step === 2) {
      const ok = answers.name.trim() && /.+@.+\..+/.test(answers.email) && answers.phone.trim()
      if (!ok) {
        setError(t('devisWizard.error.required'))
        return
      }
      // fire the brief to Web3Forms (non-blocking) then reveal the booking step
      void submitDevisBrief(answers, {
        sector: answers.sector ? t('devisWizard.sectors.' + answers.sector) : '—',
        sourcing: answers.sourcing ? t('devisWizard.sourcing.' + answers.sourcing) : '—',
        zones: answers.zones.map((z) => t('devisWizard.zones.' + z)).join(', '),
        platform: answers.platform ? t('devisWizard.platforms.' + answers.platform) : '—',
      })
    }
    setStep((s) => Math.min(s + 1, STEPS - 1))
  }

  const prev = () => {
    setError(null)
    setStep((s) => Math.max(s - 1, 0))
  }

  return (
    <div className="relative">
      {/* progress */}
      <div className="mb-7 flex items-center gap-1.5" aria-hidden>
        {Array.from({ length: STEPS }).map((_, i) => (
          <span
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i <= step ? 'bg-accent-600' : 'bg-ink-200'}`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={step}
          initial={reduce ? false : { opacity: 0, x: 18 }}
          animate={{ opacity: 1, x: 0 }}
          exit={reduce ? undefined : { opacity: 0, x: -18 }}
          transition={{ duration: 0.3, ease: EASE }}
        >
          {step === 0 && <StepProducts answers={answers} update={update} />}
          {step === 1 && <StepVolumes answers={answers} update={update} />}
          {step === 2 && <StepContact answers={answers} update={update} />}
          {step === 3 && <StepBooking answers={answers} booked={booked} onBooked={onBooked} active={active} />}
        </motion.div>
      </AnimatePresence>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600"
        >
          <svg className="mt-0.5 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
          </svg>
          {error}
        </motion.p>
      )}

      {/* nav */}
      {step < 3 ? (
        <div className="mt-8 flex items-center gap-3">
          {step > 0 && (
            <button
              type="button"
              onClick={prev}
              className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-white/70 px-5 py-3 text-sm font-medium text-accent-800 transition-colors hover:border-accent-300 hover:bg-ink-100"
            >
              {t('devisWizard.nav.prev')}
            </button>
          )}
          <button
            type="button"
            onClick={next}
            className="group relative ml-auto inline-flex items-center gap-2 overflow-hidden rounded-full bg-accent-700 px-6 py-3.5 text-sm font-semibold text-white shadow-[0_16px_50px_-12px_rgba(59,93,255,0.65)] transition-colors hover:bg-accent-800"
          >
            <span aria-hidden className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-[1100ms] ease-out group-hover:translate-x-full" />
            <span className="relative">{step === 2 ? t('devisWizard.nav.seeEstimate') : t('devisWizard.nav.next')}</span>
            <span className="relative flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
        </div>
      ) : (
        !booked && (
          <div className="mt-6">
            <button
              type="button"
              onClick={prev}
              className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-white/70 px-5 py-3 text-sm font-medium text-accent-800 transition-colors hover:border-accent-300 hover:bg-ink-100"
            >
              {t('devisWizard.nav.prev')}
            </button>
          </div>
        )
      )}
    </div>
  )
}
